import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, RefreshCw } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal that emails a branded FendanaCor compliance PDF to an external recipient.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   authToken: string
 *   doc: { category, filename, title }
 */
export default function EmailDocumentDialog({ open, onOpenChange, authToken, doc }) {
    const [form, setForm] = useState({
        recipient_email: '',
        recipient_name: '',
        trust_name: '',
        salutation: 'Dear',
        custom_note: '',
        sender_signature: '',
    });
    const [sending, setSending] = useState(false);

    const reset = () => setForm({
        recipient_email: '',
        recipient_name: '',
        trust_name: '',
        salutation: 'Dear',
        custom_note: '',
        sender_signature: '',
    });

    const submit = async () => {
        if (!form.recipient_email || !/\S+@\S+\.\S+/.test(form.recipient_email)) {
            toast.error('Please enter a valid recipient email');
            return;
        }
        if (!doc?.category || !doc?.filename) return;

        setSending(true);
        try {
            await axios.post(
                `${API}/admin/compliance-documents/${doc.category}/${doc.filename}/email`,
                form,
                { headers: { Authorization: `Bearer ${authToken}` } },
            );
            toast.success(`Sent "${doc.title}" to ${form.recipient_email}`);
            reset();
            onOpenChange(false);
        } catch (e) {
            const detail = e?.response?.data?.detail || 'Failed to send email';
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="email-document-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-teal-600" />
                        Email branded PDF
                    </DialogTitle>
                    <DialogDescription>
                        Sending: <strong>{doc?.title || ''}</strong> · attached as PDF with FendanaCor branding.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="recipient_email">Recipient email *</Label>
                        <Input
                            id="recipient_email"
                            type="email"
                            value={form.recipient_email}
                            onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                            placeholder="ig.lead@nhs.net"
                            data-testid="email-recipient-input"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="salutation">Salutation</Label>
                            <Input
                                id="salutation"
                                value={form.salutation}
                                onChange={(e) => setForm({ ...form, salutation: e.target.value })}
                                placeholder="Dear"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="recipient_name">Recipient name</Label>
                            <Input
                                id="recipient_name"
                                value={form.recipient_name}
                                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                                placeholder="Dr. Sarah Patel"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="trust_name">Trust / Organisation name</Label>
                        <Input
                            id="trust_name"
                            value={form.trust_name}
                            onChange={(e) => setForm({ ...form, trust_name: e.target.value })}
                            placeholder="Royal Devon NHS Foundation Trust"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="custom_note">Custom note (optional)</Label>
                        <Textarea
                            id="custom_note"
                            rows={3}
                            value={form.custom_note}
                            onChange={(e) => setForm({ ...form, custom_note: e.target.value })}
                            placeholder="As discussed in our call last week, please find the proposal attached…"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="sender_signature">Signature (optional)</Label>
                        <Input
                            id="sender_signature"
                            value={form.sender_signature}
                            onChange={(e) => setForm({ ...form, sender_signature: e.target.value })}
                            placeholder="Dr. Olufemi · FendanaCor"
                        />
                    </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={sending}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid="send-email-btn"
                    >
                        {sending
                            ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            : <Send className="w-4 h-4 mr-2" />}
                        Send email
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
